import { type KeyboardEvent, type ReactNode, type Ref, useId, useImperativeHandle } from 'react';

import { Field, FieldLabel } from '@nesso/ui/primitives/field';

import { SearchComboboxInput } from './search-combobox/search-combobox-input';
import { SearchComboboxResults } from './search-combobox/search-combobox-results';
import { SearchComboboxRows } from './search-combobox/search-combobox-rows';
import { useSearchCombobox } from './use-search-combobox';

const DEFAULT_MAX_VISIBLE_ROWS = 8;

type Props<T> = {
    variant: 'inline' | 'navigation';
    layout: 'dropdown' | 'panel';
    items: T[];
    getKey: (item: T) => string;
    getLabel: (item: T) => string;
    isItemDisabled?: (item: T) => boolean;
    renderItemSuffix?: (item: T) => ReactNode;
    searchTerm: string;
    onSearchTermChange: (value: string) => void;
    hasData: boolean;
    isError: boolean;
    errorMessage?: string;
    selectedLabel?: string;
    onSelect: (item: T) => void;
    onClear: () => void;
    onClose?: () => void;
    emptyAction?: { label: string; onAction: () => void };
    hint?: (hintId: string) => ReactNode;
    label?: string;
    placeholder?: string;
    resultsLabel?: string;
    maxVisibleRows?: number;
    hasError?: boolean;
    disabled?: boolean;
    leading?: ReactNode;
    ref?: Ref<HTMLInputElement>;
};

export function SearchCombobox<T>({
    variant,
    layout,
    items,
    getKey,
    getLabel,
    isItemDisabled,
    renderItemSuffix,
    searchTerm,
    onSearchTermChange,
    hasData,
    isError,
    errorMessage,
    selectedLabel,
    onSelect,
    onClear,
    onClose,
    emptyAction,
    hint,
    label,
    placeholder,
    resultsLabel = 'Search results',
    maxVisibleRows = DEFAULT_MAX_VISIBLE_ROWS,
    hasError = false,
    disabled,
    leading,
    ref,
}: Props<T>) {
    const instanceId = useId();
    const INPUT_ID = `search-combobox-${instanceId}`;
    const HINT_ID = `search-combobox-hint-${instanceId}`;
    const RESULTS_ID = `search-combobox-results-${instanceId}`;

    const {
        isOpen,
        setIsOpen,
        highlightedIndex,
        setHighlightedIndex,
        inputRef,
        itemRefs,
        handleKeyDown: handleComboboxKeyDown,
        selectItem,
    } = useSearchCombobox({ items, getKey, isItemDisabled, searchTerm, onSelect });

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [inputRef]);

    const handleClear = () => {
        onClear();
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape' && onClose) {
            event.preventDefault();
            onClose();
            return;
        }
        handleComboboxKeyDown(event);
    };

    const activeOptionId =
        highlightedIndex >= 0 && items[highlightedIndex]
            ? `${RESULTS_ID}-option-${highlightedIndex}`
            : undefined;

    const hasHint = hint !== undefined && layout === 'dropdown';

    const input = (
        <SearchComboboxInput
            ref={inputRef}
            id={INPUT_ID}
            describedBy={hasHint ? HINT_ID : undefined}
            controls={RESULTS_ID}
            activeOptionId={activeOptionId}
            value={selectedLabel ?? searchTerm}
            onChange={onSearchTermChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            onClearSearch={handleClear}
            onCloseSearch={layout === 'dropdown' ? onClose : undefined}
            isOpen={isOpen}
            isInvalid={hasError}
            placeholder={placeholder}
            disabled={disabled}
            variant={variant}
        />
    );

    const renderRows = (as: 'menu-item' | 'list-item') => (
        <SearchComboboxRows
            hasData={hasData}
            isError={isError}
            errorMessage={errorMessage}
            items={items}
            getKey={getKey}
            getLabel={getLabel}
            isItemDisabled={isItemDisabled}
            renderItemSuffix={renderItemSuffix}
            searchTerm={searchTerm}
            highlightedIndex={highlightedIndex}
            optionIdPrefix={RESULTS_ID}
            onSelect={selectItem}
            onMouseEnter={setHighlightedIndex}
            itemRefs={itemRefs}
            emptyAction={emptyAction}
            as={as}
        />
    );

    if (layout === 'panel') {
        const rows = renderRows('list-item');

        return (
            <>
                <div className="flex shrink-0 items-center gap-2.5 bg-background py-4 pr-4 pl-2">
                    {leading}
                    {input}
                </div>
                {isOpen && (
                    <div id={RESULTS_ID} className="min-h-0 overflow-y-auto bg-popover px-2 py-1">
                        {items.length > 0 ? (
                            <div role="listbox" aria-label={resultsLabel}>
                                {rows}
                            </div>
                        ) : (
                            rows
                        )}
                    </div>
                )}
            </>
        );
    }

    return (
        <Field data-invalid={hasError}>
            {label && <FieldLabel htmlFor={INPUT_ID}>{label}</FieldLabel>}
            <SearchComboboxResults
                id={RESULTS_ID}
                isOpen={isOpen}
                maxVisibleRows={maxVisibleRows}
                onOpenChange={setIsOpen}
                onEscape={() => onClose?.()}
                inputRef={inputRef}
                rows={renderRows('menu-item')}
            >
                {input}
            </SearchComboboxResults>
            {hasHint && hint(HINT_ID)}
        </Field>
    );
}
